import {
  type KeyboardEvent,
  type RefObject,
  useEffect,
  useRef,
  useState,
} from "react";

import { normalizeWorkspaceSymbol } from "./workspace/workspace.utils";

export interface TitleSymbolFieldControllerParams {
  value?: string;
  onCommit: (symbol: string) => void;
  error?: string;
}

export interface TitleSymbolFieldControllerValue {
  value: string | undefined;
  error: string | undefined;
  editing: boolean;
  draft: string;
  inputRef: RefObject<HTMLInputElement>;
  onStartEditing: () => void;
  onDraftChange: (draft: string) => void;
  onBlur: () => void;
  onKeyDown: (event: KeyboardEvent<HTMLInputElement>) => void;
}

export const useTitleSymbolFieldController = ({
  value,
  onCommit,
  error,
}: TitleSymbolFieldControllerParams): TitleSymbolFieldControllerValue => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [editing]);

  const close = () => {
    setEditing(false);
    setDraft("");
  };

  const commit = () => {
    const next = normalizeWorkspaceSymbol(draft);
    if (!next) {
      close();
      return;
    }
    onCommit(next);
    close();
  };

  const onStartEditing = () => {
    setDraft(value ?? "");
    setEditing(true);
  };

  const onDraftChange = (nextDraft: string) => {
    setDraft(nextDraft);
  };

  const onBlur = () => {
    close();
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Escape") {
      event.preventDefault();
      close();
      return;
    }
    if (event.key === "Enter") {
      event.preventDefault();
      commit();
    }
  };

  return {
    value,
    error,
    editing,
    draft,
    inputRef,
    onStartEditing,
    onDraftChange,
    onBlur,
    onKeyDown,
  };
};
